import z from "zod";
import { BaseSchema, ImageSchema, LanguageEnum } from "../common";
import { PlanSchema } from "../plan/entity";
import { BadgesEnum, GoalEnum, LocationSchema, PaymentMethodEnum, ProjectStatusEnum, TypeEnum } from "./common";
import { ProjectTranslationSchema } from "./translation";

const CommonProjectSchema = z.object({
  ...BaseSchema.shape,
  badges: BadgesEnum.array(),
  code: z.string(),
  districtId: z.uuidv7(),
  goals: GoalEnum.array(),
  images: z.string().array(),
  location: LocationSchema,
  payment: PaymentMethodEnum.array(),
  status: ProjectStatusEnum,
  types: TypeEnum.array(),
  video: z.url().optional(),
  thumbnail: z.string().optional(),
  altText: z.string().optional(),
  distanceCount: z.number().min(0),
  district: z.string(),
  featureCount: z.number().min(0),
  maxArea: z.number().min(0).optional(),
  maxBathroom: z.number().min(0).optional(),
  minBathroom: z.number().min(0).optional(),
  maxBedroom: z.number().min(0).optional(),
  minBedroom: z.number().min(0).optional(),
  maxLivingRoom: z.number().min(0).optional(),
  minLivingRoom: z.number().min(0).optional(),
  maxPrice: z.number().optional(),
  minArea: z.number().min(0).optional(),
  minPrice: z.number().optional(),
  planCount: z.number().min(0),
});

/* ------------------------------------------------ Public ------------------------------------------------ */

export const PublicProjectSchema = z.object({
  ...CommonProjectSchema.shape,
  city: z.string(),
  title: z.string(),
  overview: z.string(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  favorite: z.boolean(),
  plans: PlanSchema.array(),
  projectDistances: z
    .object({ distanceId: z.uuidv7(), name: z.string(), slug: z.string(), distance: z.number() })
    .array(),
  projectFeatures: z.object({ featureId: z.uuidv7(), name: z.string(), slug: z.string() }).array(),
});

export const PublicProjectSummarySchema = z.object({
  id: z.uuidv7(),
  code: z.string(),
  slug: z.string().optional(),
  title: z.string(),
  overview: z.string(),
  badges: BadgesEnum.array(),
  thumbnail: z.string().optional(),
  altText: z.string().optional(),
  status: ProjectStatusEnum,
  payment: PaymentMethodEnum.array(),
  goals: GoalEnum.array(),
  types: TypeEnum.array(),
  minPrice: z.number().optional(),
  maxPrice: z.number().optional(),
  minArea: z.number().optional(),
  maxArea: z.number().optional(),
  minBedroom: z.number().optional(),
  maxBedroom: z.number().optional(),
  minLivingRoom: z.number().optional(),
  maxLivingRoom: z.number().optional(),
  maxBathroom: z.number().min(0).optional(),
  minBathroom: z.number().min(0).optional(),
  video: z.url().optional(),
  images: z.string().array(),
  favorite: z.boolean(),
  city: z.string(),
  district: z.string(),
});

/* ------------------------------------------------- admin ------------------------------------------------ */

export const AdminProjectSchema = z.object({
  ...CommonProjectSchema.shape,
  city: z.string(),
  cityAr: z.string(),
  districtAr: z.string(),
  projectName: z.string().optional(),
  driveLink: z.string().optional(),
  minPriceUsd: z.number().positive().optional(),
  maxPriceUsd: z.number().positive().optional(),
  translations: ProjectTranslationSchema.array(),
  plans: PlanSchema.extend({ priceUsd: z.number().positive().optional() }).array(),
  projectFeatures: z
    .object({ featureId: z.uuidv7(), name: z.string(), nameAr: z.string(), slug: z.string() })
    .array(),
  projectDistances: z
    .object({ distanceId: z.uuidv7(), name: z.string(), nameAr: z.string(), slug: z.string(), distance: z.number() })
    .array(),
});

export const AdminProjectSummarySchema = z.object({
  id: z.uuidv7(),
  code: z.string(),
  projectName: z.string().optional(),
  titleEn: z.string().optional(),
  titleAr: z.string().optional(),
  titleTr: z.string().optional(),
  titleFa: z.string().optional(),
  planCount: z.number().min(0),
  publishStatus: z.enum(["active", "archived"]),
  distanceCount: z.number().min(0),
  featureCount: z.number().min(0),
  bathrooms: z.string().optional(),
  driveLink: z.string().optional(),
  firstPublishedAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/* ----------------------------------------------- Mutation ----------------------------------------------- */

export const CreateProjectSchema = z.object({
  altText: z.string().optional(),
  badges: BadgesEnum.array(),
  code: z.string().trim().min(3),
  distances: z.object({ distance: z.number(), distanceId: z.uuidv7() }).array(),
  districtId: z.uuidv7(),
  featureIds: z.uuidv7().array(),
  goals: GoalEnum.array(),
  images: z.object({
    items: ImageSchema.nonoptional().array(),
    thumbnail: z.string().optional(),
  }),
  location: LocationSchema,
  payment: PaymentMethodEnum.array()
    .min(1, "Please answer whether installments are available")
    .refine((payment) => payment.includes("cash"), "Please answer whether installments are available"),
  status: ProjectStatusEnum,
  // translations: ProjectTranslationMapSchema,
  lang: LanguageEnum,
  title: z.string(),
  overview: z.string(),
  metaTitle: z.string().trim().optional(),
  metaDescription: z.string().trim().optional(),
  types: TypeEnum.array(),
  video: z.url().optional().catch(undefined),
  projectName: z.string().optional(),
  driveLink: z.string().optional(),
});

export const UpdateProjectSchema = CreateProjectSchema.extend({ id: z.uuidv7() });

/* ------------------------------------------------- Draft ------------------------------------------------ */

export const ProjectDraftDataSchema = CreateProjectSchema.extend({
  code: z.string(),
  districtId: z.string(),
  featureIds: z.string().array(),
  distances: z.object({ distance: z.number(), distanceId: z.string() }).array(),
})
  .partial()
  .extend({ payment: PaymentMethodEnum.array().optional() });

export const ProjectDraftSchema = z.object({
  ...BaseSchema.shape,
  name: z.string(),
  data: ProjectDraftDataSchema,
});

export const ProjectDraftSummarySchema = ProjectDraftSchema.omit({ data: true }).extend({
  code: z.string().optional(),
  driveLink: z.string().optional(),
});

export const SeedProjectSchema = PublicProjectSchema.omit({ createdAt: true, updatedAt: true });

export const GenerateProjectOverviewSchema = z.object({
  language: LanguageEnum,
  params: z.string(),
  previousOverview: z.string().optional(),
});

export const TranslateOverviewSchema = z.object({
  language: LanguageEnum,
  arabic: z.string(),
});
